import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Zap,
  Clock,
  LayoutGrid,
  AlertTriangle,
  CheckCircle2,
  AlertCircle,
  ArrowUpRight,
  RefreshCw,
  Loader2,
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { api } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useProject } from '../../context/ProjectContext';

const STATUS_CONFIG = {
  good: { label: 'Good', icon: CheckCircle2, bg: 'bg-emerald-50 dark:bg-emerald-900/20', text: 'text-emerald-700', badge: 'border-emerald-200 text-emerald-700 bg-emerald-50' },
  needs_improvement: { label: 'Needs Improvement', icon: AlertTriangle, bg: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-700', badge: 'border-amber-200 text-amber-700 bg-amber-50' },
  poor: { label: 'Poor', icon: AlertCircle, bg: 'bg-red-50 dark:bg-red-900/20', text: 'text-red-700', badge: 'border-red-200 text-red-700 bg-red-50' },
};

function formatMs(ms) {
  if (ms == null) return '—';
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms)}ms`;
}

function CwvTile({ label, value, unit, threshold_good, threshold_poor, icon: Icon, color }) {
  const numVal = typeof value === 'number' ? value : null;
  const status = numVal === null ? null
    : numVal <= threshold_good ? 'good'
    : numVal <= threshold_poor ? 'needs_improvement'
    : 'poor';

  const cfg = status ? STATUS_CONFIG[status] : null;
  const StatusIcon = cfg?.icon;

  return (
    <div className={`p-5 rounded-xl border ${cfg ? cfg.bg : 'bg-slate-50 dark:bg-slate-800/30'} border-slate-200 dark:border-slate-800`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center`} style={{ background: `${color}20` }}>
            <Icon className="w-4 h-4" style={{ color }} />
          </div>
          <span className="text-sm font-medium text-slate-600 dark:text-slate-400">{label}</span>
        </div>
        {StatusIcon && (
          <StatusIcon className={`w-4 h-4 ${cfg.text}`} />
        )}
      </div>
      <p className="text-3xl font-bold font-heading mb-1">
        {unit === 'ms' ? formatMs(value) : value != null ? value.toFixed(3) : '—'}
      </p>
      {status && (
        <Badge variant="outline" className={`text-xs ${cfg.badge}`}>
          {cfg.label}
        </Badge>
      )}
      <p className="text-xs text-slate-400 mt-2">
        Good: {unit === 'ms' ? formatMs(threshold_good) : threshold_good} · Poor: {unit === 'ms' ? formatMs(threshold_poor) : threshold_poor}
      </p>
    </div>
  );
}

export default function CoreWebVitalsPage() {
  const navigate = useNavigate();
  const { selectedProjectId } = useProject();
  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trendDays, setTrendDays] = useState(30);
  const [strategy, setStrategy] = useState('mobile');

  const fetchData = async () => {
    setLoading(true);
    const projectParams = selectedProjectId ? { project_id: selectedProjectId } : {};
    try {
      const [summaryRes, trendRes] = await Promise.all([
        api.get('/core-web-vitals/summary', { params: projectParams }),
        api.get('/core-web-vitals/trend', { params: { days: trendDays, strategy, ...projectParams } }),
      ]);
      setSummary(summaryRes.data);
      setTrend(trendRes.data);
    } catch (e) {
      setSummary({ available: false });
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, [trendDays, strategy, selectedProjectId]);

  // Poll while CWV backfill is in progress (scan completed but CWV not yet written)
  useEffect(() => {
    if (!summary?.pending) return;
    const interval = setInterval(async () => {
      const projectParams = selectedProjectId ? { project_id: selectedProjectId } : {};
      try {
        const res = await api.get('/core-web-vitals/summary', { params: projectParams });
        if (res.data?.available) {
          setSummary(res.data);
          const trendRes = await api.get('/core-web-vitals/trend', { params: { days: trendDays, strategy, ...projectParams } });
          setTrend(trendRes.data);
        } else {
          setSummary(res.data);
        }
      } catch { /* ignore */ }
    }, 5000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary?.pending, selectedProjectId]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-64 rounded-lg bg-slate-200 dark:bg-slate-800 animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-36 rounded-xl bg-slate-100 dark:bg-slate-800 animate-pulse" />
          ))}
        </div>
        <div className="h-64 rounded-xl bg-slate-100 dark:bg-slate-800 animate-pulse" />
      </div>
    );
  }

  if (!summary?.available) {
    const isPending = summary?.pending;
    return (
      <div className="space-y-6">
        <div>
          <h1 className="font-heading text-2xl font-bold mb-1">Core Web Vitals</h1>
          <p className="text-slate-600 dark:text-slate-400">Site performance metrics from Google PageSpeed Insights</p>
        </div>
        <Card className="gds-card">
          <CardContent className="p-12 flex flex-col items-center text-center">
            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-4 ${isPending ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-indigo-100 dark:bg-indigo-900/30'}`}>
              {isPending
                ? <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                : <Zap className="w-8 h-8 text-indigo-500" />
              }
            </div>
            <h3 className="font-heading font-semibold text-xl mb-2">
              {isPending ? 'Fetching Core Web Vitals…' : 'No CWV data yet'}
            </h3>
            <p className="text-slate-500 mb-6 max-w-md">
              {isPending
                ? 'Your on-page scan finished. Core Web Vitals are being retrieved from PageSpeed Insights — this usually takes under a minute.'
                : 'Run your first on-page scan to capture Core Web Vitals (LCP, INP, CLS) for your site.'
              }
            </p>
            {!isPending && (
              <Button className="gds-btn-primary" onClick={() => navigate('/dashboard/onpage')}>
                Run On-Page Scan
                <ArrowUpRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const avg = summary.averages || {};
  const counts = summary.counts || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold mb-1">Core Web Vitals</h1>
          <p className="text-slate-600 dark:text-slate-400">
            Site performance metrics · {counts.total || 0} page{counts.total !== 1 ? 's' : ''} tracked
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Site-wide health bar */}
      <div className="flex gap-3">
        {[
          { label: 'Good', count: counts.good, color: 'bg-emerald-100 text-emerald-700' },
          { label: 'Needs Improvement', count: counts.needs_improvement, color: 'bg-amber-100 text-amber-700' },
          { label: 'Poor', count: counts.poor, color: 'bg-red-100 text-red-700' },
        ].map(({ label, count, color }) =>
          count > 0 ? (
            <div key={label} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm ${color}`}>
              <span className="font-bold">{count}</span>
              <span>{label}</span>
            </div>
          ) : null
        )}
      </div>

      {/* KPI tiles */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="grid grid-cols-1 md:grid-cols-3 gap-4"
      >
        <CwvTile
          label="LCP (Largest Contentful Paint)"
          value={avg.lcp}
          unit="ms"
          threshold_good={2500}
          threshold_poor={4000}
          icon={Clock}
          color="#6366f1"
        />
        <CwvTile
          label="INP (Interaction to Next Paint)"
          value={avg.inp}
          unit="ms"
          threshold_good={200}
          threshold_poor={500}
          icon={Zap}
          color="#06b6d4"
        />
        <CwvTile
          label="CLS (Cumulative Layout Shift)"
          value={avg.cls}
          unit="score"
          threshold_good={0.1}
          threshold_poor={0.25}
          icon={LayoutGrid}
          color="#f59e0b"
        />
      </motion.div>

      {/* Trend chart */}
      {trend?.trend?.length > 1 && (
        <Card className="gds-card">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <CardTitle className="font-heading">Trend</CardTitle>
                <CardDescription>Average CWV values over time</CardDescription>
              </div>
              <div className="flex gap-2 flex-wrap">
                <div className="flex gap-1">
                  {[7, 30, 90].map(d => (
                    <Button
                      key={d}
                      variant={trendDays === d ? 'default' : 'ghost'}
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={() => setTrendDays(d)}
                    >
                      {d}d
                    </Button>
                  ))}
                </div>
                <div className="flex gap-1">
                  {['mobile', 'desktop'].map(s => (
                    <Button
                      key={s}
                      variant={strategy === s ? 'default' : 'ghost'}
                      size="sm"
                      className="h-7 px-2 text-xs capitalize"
                      onClick={() => setStrategy(s)}
                    >
                      {s}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trend.trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" stroke="#94a3b8" fontSize={11} tickLine={false} />
                  <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} />
                  <Tooltip
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="lcp" stroke="#6366f1" strokeWidth={2} dot={false} name="LCP (ms)" />
                  <Line type="monotone" dataKey="inp" stroke="#06b6d4" strokeWidth={2} dot={false} name="INP (ms)" />
                  <Line type="monotone" dataKey="performance_score" stroke="#22c55e" strokeWidth={2} dot={false} name="Perf Score" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Per-page table */}
      {summary.pages?.length > 0 && (
        <Card className="gds-card">
          <CardHeader>
            <CardTitle className="font-heading">Per-Page Breakdown</CardTitle>
            <CardDescription>Latest CWV for each scanned page. Sorted worst-first.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 dark:border-slate-800">
                    <th className="text-left py-2 px-3 text-xs text-slate-500 font-medium">Page</th>
                    <th className="text-right py-2 px-3 text-xs text-slate-500 font-medium">LCP</th>
                    <th className="text-right py-2 px-3 text-xs text-slate-500 font-medium">INP</th>
                    <th className="text-right py-2 px-3 text-xs text-slate-500 font-medium">CLS</th>
                    <th className="text-right py-2 px-3 text-xs text-slate-500 font-medium">Score</th>
                    <th className="text-center py-2 px-3 text-xs text-slate-500 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50 dark:divide-slate-800/50">
                  {summary.pages.map((page, i) => {
                    const cfg = STATUS_CONFIG[page.status] || STATUS_CONFIG.needs_improvement;
                    return (
                      <tr
                        key={i}
                        className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors cursor-pointer"
                        onClick={() => navigate('/dashboard/onpage')}
                      >
                        <td className="py-2.5 px-3">
                          <span className="text-xs text-slate-700 dark:text-slate-300 truncate block max-w-xs">
                            {page.url}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 text-right text-xs font-mono">{formatMs(page.lcp)}</td>
                        <td className="py-2.5 px-3 text-right text-xs font-mono">{formatMs(page.inp)}</td>
                        <td className="py-2.5 px-3 text-right text-xs font-mono">{page.cls != null ? page.cls.toFixed(3) : '—'}</td>
                        <td className="py-2.5 px-3 text-right text-xs font-medium">{page.performance_score ?? '—'}</td>
                        <td className="py-2.5 px-3 text-center">
                          <Badge variant="outline" className={`text-xs ${cfg.badge}`}>
                            {cfg.label}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
